import { useState, useCallback } from 'react'

import { initRequestParam } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'
import { useApiCacheStore } from 'store/apiCacheStore'
import { normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'
// import { useCurrentStatusStore } from 'store/currentStatusStore'

import type {
  GetMstCentersCenterGetRequest,
  ResponseResult,
  ResponseUpdate,
  MasterCenterUpdateMasterCenterUpdatePostRequest,
  Center,
  MstCenter,
} from 'api-client'
import type { ErrorResponse } from 'types/types'


export interface AutocompleteCenter {
  label: string | null
  centerId?: number | null
  orgCenterId?: string | null
}

export interface MstCenterSeq extends MstCenter{
  id: string
}

export interface MstCentersSeq extends Center{
  list: MstCenterSeq[]
}

export const useCenter = () => {
  const [centers, setCenters] = useState<MstCenter[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [result, setResult] = useState<ResponseResult>()
  const [autocompletCenters, setAutocompleteCenters] = useState<AutocompleteCenter[]>([])
  const [centerSearch, setCenterSearch] = useState<MstCentersSeq>(initRequestParam)
  const [centerSearchResult, setCenterSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [updateResult, setUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const { loginUserInfo } = useAuthContext()
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  // const { setCurrentStatuses } = useCurrentStatusStore()
  const { callApiWithCache } = useApiCacheStore()

  const fetchCenterSearch = useCallback(async (searchParam: GetMstCentersCenterGetRequest, forceRefresh = false) => {
    setCenterSearch(initRequestParam)
    setResult(undefined)
    setIsLoading(true)

    try {
      const response = await callApiWithCache(
        '/center',
        searchParam,
        async () => {
          // Actual API call
          const api = await getAPI()
          if (!api) throw new Error('API initialization failed')
          return api.getMstCentersCenterGet(searchParam)
        },
        systemDate,
        forceRefresh // Pass forceRefresh to cache
      )

      setCenterSearch({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      
      // SelectCenterコンポーネントのデータバインド形式に合わせる
      const centersMap = response.data.list.map((value) => ({
        label: value.centerName,
        centerId: value.centerId,
        orgCenterId: value.orgCenterId
      }))
      setAutocompleteCenters(centersMap)
      setCenterSearchResult(response.result)
      // setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
    } catch (error) {
      const errorResponse = error as ErrorResponse
      setCenterSearchResult({
        status: 'error',
        message: errorResponse.response && errorResponse.response.status === 422 ? '入力値に誤りがあります。' : `センターマスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    } finally {
      setIsLoading(false)
    }
  }, [callApiWithCache])

  const fetchCenterUpdate = async (body: MasterCenterUpdateMasterCenterUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setResult(undefined)
    // setCenterUpdateIsLoading(true);
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.masterCenterUpdateMasterCenterUpdatePost(body);
      
      setResult(response.result)
      setUpdateResult(response);

      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || 'センターマスタの更新でエラーが発生しました。');
        Object.assign(error, {
            ...response
        });
        throw error;
      }

      // Clear cache for center after successful update
      const { clearCache } = useApiCacheStore.getState()
      clearCache('/center')
      
      return response;
    } catch (error) {
        if (error && typeof error === 'object' && 'result' in error) {
            const apiResponse = error as ResponseUpdate; 
            await new Promise<void>(resolve => {
              setUpdateResult(apiResponse);
              resolve();
            });
            return await Promise.reject(apiResponse);
        }
        const { response } = error as any;
        const errorResponse = {
            errors: [],
            result: {
                status: 'error',
                message: response && response.status === 422 ? '入力値に誤りがあります。' : 'センターマスタの更新でエラーが発生しました。',
                systemDate: null,
                pageNo: 0,
                systemInfo: null,
            },
            updateCount: null,
            lastUpdateDatetime: null,
        };
        await new Promise<void>(resolve => {
          setUpdateResult(errorResponse);
            setResult(errorResponse.result)
            resolve();
        });
        return await Promise.reject(errorResponse);

    } finally {
      // setCenterUpdateIsLoading(false);
      setIsUpdating(false);
    }
  };


  return {
  	result,
  	isLoading,
  	isUpdating,
    centers,
    autocompletCenters,
    
    centerSearch,
    fetchCenterSearch,
    centerSearchResult,
    // centerSearchIsLoading,

    fetchCenterUpdate,
    updateResult,
    // centerUpdateIsLoading,
  }
}